import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  Droplet, Flame, Shield, Hammer, Wind, Home, Fence, PaintBucket,
  Phone, Mail, MapPin, Clock, Menu, ChevronRight, Send, Instagram,
  Star, CheckCircle, ArrowRight, Building2, Users, Award
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  droplet: Droplet,
  flame: Flame,
  shield: Shield,
  hammer: Hammer,
  wind: Wind,
  home: Home,
  fence: Fence,
  paintbrush: PaintBucket
};

// ============ COMPONENTS ============

// Emergency Banner
const EmergencyBanner = () => (
  <div className="emergency-banner" data-testid="emergency-banner">
    <Marquee speed={50} gradient={false}>
      <span className="mx-8">24/7 EMERGENCY SERVICE</span>
      <span className="mx-8">•</span>
      <span className="mx-8">WATER DAMAGE</span>
      <span className="mx-8">•</span>
      <span className="mx-8">FIRE RESTORATION</span>
      <span className="mx-8">•</span>
      <span className="mx-8">CALL NOW: (512) 718-3553</span>
      <span className="mx-8">•</span>
      <span className="mx-8">MOLD REMEDIATION</span>
      <span className="mx-8">•</span>
      <span className="mx-8">FREE INSPECTIONS</span>
      <span className="mx-8">•</span>
    </Marquee>
  </div>
);

// Header
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className={`header ${isScrolled ? "scrolled" : ""}`} data-testid="header">
      <div className="header-content">
        <div className="logo" data-testid="logo">
          <img src="https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/8w2t4a51_E2A5A613-25B8-4838-88DA-4BC61FBC2F68.png" alt="Giraffe Home & Commercial Services" className="logo-image" />
          <div className="logo-text-wrapper">
            <span className="logo-text">GIRAFFE</span>
            <span className="logo-sub">Home & Commercial</span>
          </div>
        </div>
        
        <nav className="nav-desktop" data-testid="nav-desktop">
          <button onClick={() => scrollToSection("services")} data-testid="nav-services">Services</button>
          <button onClick={() => scrollToSection("about")} data-testid="nav-about">About</button>
          <button onClick={() => scrollToSection("gallery")} data-testid="nav-gallery">Gallery</button>
          <button onClick={() => scrollToSection("testimonials")} data-testid="nav-testimonials">Reviews</button>
          <button onClick={() => scrollToSection("contact")} data-testid="nav-contact">Contact</button>
        </nav>

        <a href="tel:5127183553" className="header-cta" data-testid="header-phone-cta">
          <Phone size={18} />
          <span>(512) 718-3553</span>
        </a>

        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} />
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            className="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            data-testid="mobile-menu"
          >
            <button onClick={() => scrollToSection("services")}>Services</button>
            <button onClick={() => scrollToSection("about")}>About</button>
            <button onClick={() => scrollToSection("gallery")}>Gallery</button>
            <button onClick={() => scrollToSection("testimonials")}>Reviews</button>
            <button onClick={() => scrollToSection("contact")}>Contact</button>
            <a href="tel:5127183553" className="mobile-phone">
              <Phone size={18} /> (512) 718-3553
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="hero" data-testid="hero-section">
      <div className="hero-overlay"></div>
      <div className="hero-content">
        <motion.p 
          className="hero-overline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          LICENSED • INSURED • TRUSTED
        </motion.p>
        <motion.h1 
          className="hero-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          YOUR ONE-STOP SHOP FOR<br />
          <span className="text-gradient">CONSTRUCTION & RESTORATION</span>
        </motion.h1>
        <motion.p 
          className="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          General construction, water damage, fire restoration, mold remediation, 
          HVAC, roofing, and more. Serving homeowners, property managers, and businesses.
        </motion.p>
        <motion.div 
          className="hero-ctas"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button 
            className="btn-primary"
            onClick={() => scrollToSection("contact")}
            data-testid="hero-quote-btn"
          >
            GET FREE QUOTE
            <ArrowRight size={18} />
          </button>
          <button 
            className="btn-mold"
            onClick={() => scrollToSection("contact")}
            data-testid="hero-mold-btn"
          >
            FREE MOLD INSPECTION
          </button>
          <a href="tel:5127183553" className="btn-emergency" data-testid="hero-emergency-btn">
            <Phone size={18} />
            24/7 EMERGENCY
          </a>
        </motion.div>
        <motion.div 
          className="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="stat">
            <span className="stat-number">27+</span>
            <span className="stat-label">Years Experience</span>
          </div>
          <div className="stat">
            <span className="stat-number">Since '97</span>
            <span className="stat-label">Serving People</span>
          </div>
          <div className="stat">
            <span className="stat-number">24/7</span>
            <span className="stat-label">Emergency Service</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios.get(`${API}/services`).then(res => setServices(res.data)).catch(console.error);
  }, []);

  const handleLearnMore = (serviceId) => {
    // Scroll to contact and set service type
    const contactSection = document.getElementById('contact');
    const serviceSelect = document.getElementById('service');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
      // Small delay to ensure scroll completes before setting value
      setTimeout(() => {
        if (serviceSelect) {
          serviceSelect.value = serviceId;
          serviceSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, 800);
    }
  };

  return (
    <section id="services" className="services-section" data-testid="services-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="overline">WHAT WE DO</p>
          <h2 className="section-title">OUR SERVICES</h2>
          <p className="section-subtitle">
            From emergency restoration to complete construction projects, 
            we handle it all with expertise and precision.
          </p>
        </motion.div>

        <div className="services-grid">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Hammer;
            return (
              <motion.div 
                key={service.id}
                className={`service-card ${service.accent ? "accent" : ""}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-card-${service.id}`}
              >
                <div className="service-icon">
                  <IconComponent size={32} />
                </div>
                {service.emergency && (
                  <span className="emergency-badge">24/7</span>
                )}
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <button 
                  className="service-link" 
                  onClick={() => handleLearnMore(service.id)}
                  data-testid={`service-link-${service.id}`}
                >
                  Learn More <ChevronRight size={16} />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Who We Serve Section
const WhoWeServe = () => (
  <section className="who-we-serve-section" data-testid="who-we-serve-section">
    <div className="container">
      <motion.div 
        className="who-we-serve-content centered"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <p className="overline">WHO WE SERVE</p>
        <h2 className="section-title">
          WE'RE HERE FOR<br />
          <span className="text-gradient">EVERYONE</span>
        </h2>
        <p className="who-we-serve-text">
          At Giraffe Home & Commercial, we're proud to help all kinds of people — 
          from families and small business owners to landlords and property managers. 
          We also partner with:
        </p>
        <ul className="who-we-serve-list">
          <li>
            <CheckCircle className="check-icon" size={20} />
            <span>Insurance adjusters and agents looking for a reliable restoration partner</span>
          </li>
          <li>
            <CheckCircle className="check-icon" size={20} />
            <span>Realtors needing quick turnarounds for property sales</span>
          </li>
          <li>
            <CheckCircle className="check-icon" size={20} />
            <span>Apartment and condo managers facing water damage or mold issues</span>
          </li>
        </ul>
        <p className="who-we-serve-text highlight">
          No matter what you're dealing with, we'll show up fast, do the job right, and treat 
          your space with the respect it deserves. You don't have to go through the mess alone — 
          we've got your back.
        </p>
      </motion.div>
    </div>
  </section>
);

// About Section
const About = () => (
  <section id="about" className="about-section" data-testid="about-section">
    <div className="container">
      <motion.div 
        className="about-content centered"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <p className="overline">ABOUT US</p>
        <h2 className="section-title">
          DISASTERS HAPPEN.<br />
          <span className="text-gradient">WE'RE HERE FOR YOU.</span>
        </h2>
        <p className="about-text">
          Here at Giraffe, we know that disasters are unfortunate and can happen at anytime. 
          Burst pipe, roof leak, fire & mold can cause serious anxiety and stress. If that 
          happens, our team of strong professionals are here for you.
        </p>
        <p className="about-text">
          Since 1997, we have been in the business of helping people all over the country 
          with any sort of emergency or construction project, big or small. If you are 
          dealing with Mold, Water damage, Fire damage or any sort of general construction — 
          Giraffe knows how to handle it. Our team is here for you to help you get back to 
          normal, as fast as possible.
        </p>
        <p className="about-text">
          We would love to serve you anyway we can. Our team of experts will check everything, 
          explain what needs to be done and have the tools and resources available for you.
        </p>
        
        <div className="about-features">
          <div className="about-feature">
            <CheckCircle className="check-icon" size={20} />
            <span>Licensed & Insured</span>
          </div>
          <div className="about-feature">
            <CheckCircle className="check-icon" size={20} />
            <span>Free Estimates</span>
          </div>
          <div className="about-feature">
            <CheckCircle className="check-icon" size={20} />
            <span>Insurance Claim Assistance</span>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// Gallery Section
const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState("Featured");
  
  const projects = [
    { id: 1, title: "Commercial Renovation - Liberty Hill PD", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/x27xxf3w_IMG_0428.jpeg", featured: true },
    { id: 2, title: "Commercial Build-Out", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/jsrq2jnz_IMG_0425.jpeg" },
    { id: 3, title: "Luxury Bathroom Remodel", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/pouxoabw_IMG_0413.jpeg", featured: true },
    { id: 4, title: "Bathroom Renovation - Before", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/vvr9jhxe_IMG_0412.jpeg" },
    { id: 5, title: "Drywall Post Mold Clearance", category: "Construction", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/n95u2psq_IMG_0423.jpeg", featured: true },
    { id: 6, title: "Laundry Room Remodel - After", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/y0zm7bgx_IMG_0410.jpeg" },
    { id: 7, title: "Laundry Room with Custom Cabinets", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/pffedxvx_IMG_0411.jpeg" },
    { id: 8, title: "Laundry Room - Before", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/dnf6f1iv_IMG_0407.jpeg" },
    { id: 9, title: "Laundry Room - In Progress", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/gctblw16_IMG_0408.jpeg" },
    { id: 10, title: "Water Damage Restoration", category: "Restoration", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/mxarnl4o_IMG_0082.jpeg", featured: true },
    { id: 11, title: "Thermal Imaging Inspection", category: "Restoration", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/a1hlw1m0_IMG_0066.jpeg" },
    { id: 12, title: "Mold Containment Setup", category: "Restoration", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/djlur5mo_IMG_0169.jpeg" },
    { id: 13, title: "Mold Remediation Barriers", category: "Restoration", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/djfqjw5x_IMG_0339.jpeg" },
    { id: 14, title: "Plumbing Repair", category: "Restoration", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/i9ob113m_IMG_0186.jpeg" },
    { id: 15, title: "Double Vanity Bathroom - Complete", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/csjb4ub9_20260406_170925.jpeg" },
    { id: 16, title: "Bathroom Vanity Installation", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/hjsuawk0_20260403_161206.jpeg" },
    { id: 17, title: "Car Wash Metal Framing", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/4gpwv6i6_IMG_0457.jpeg" },
    { id: 18, title: "Car Wash Wall Installation", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/gqi5n2yg_IMG_0447.jpeg" },
    { id: 19, title: "Custom Kitchen Cabinets", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/l84mh7fh_IMG_0454.jpeg" },
    { id: 20, title: "Cabinet Installation", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/xayvlj9e_IMG_0453.jpeg" },
    { id: 21, title: "Wood Framing", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/pb0yf8ag_IMG_0448.jpeg" },
    { id: 22, title: "Siding Installation", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/hlgehydj_IMG_0450.jpeg" },
    { id: 23, title: "Stone Patio & Fire Pit", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/7sbdgxj8_IMG_0451.jpeg" },
    { id: 24, title: "Walmart Metal Framing - Nebraska", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/f86alxrx_IMG_0449.jpeg" },
    { id: 25, title: "Walmart Structure Build - Nebraska", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/wl98jpc3_IMG_0456.jpeg", featured: true },
    { id: 26, title: "Commercial Exterior Work", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/rbc4f0zg_IMG_0446.jpeg" },
    { id: 27, title: "Metal Truss Installation", category: "Commercial", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/f301q2hf_IMG_0443.jpeg" },
    { id: 28, title: "Roofing Installation", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/r45a3ns8_IMG_0445.jpeg" },
    { id: 29, title: "Custom Woodwork", category: "Interior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/va82kypq_IMG_0444.jpeg" },
    { id: 30, title: "Drywall for Perry Homes", category: "Construction", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/6rh53356_Resized_20250722_115537.jpeg" },
    { id: 31, title: "Drywall Installation", category: "Construction", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/ai9n0l24_Resized_20250722_171401.jpeg" },
    { id: 32, title: "Drywall Finishing", category: "Construction", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/2prp4rn3_Resized_20250722_152001.jpeg" },
    { id: 33, title: "Kitchen Mold Containment", category: "Restoration", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/uolah5ok_IMG_2528.jpeg" },
    { id: 34, title: "Water Damage Drying Process", category: "Restoration", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/lmgeg7py_IMG_2473.jpeg" },
    { id: 35, title: "Wire Panel Fence", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/g2ykifww_IMG_0458.jpeg" },
    { id: 36, title: "Privacy Fence with Gate", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/3q36juam_IMG_0459.jpeg" },
    { id: 37, title: "Horizontal Wood Fence", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/vrst3der_IMG_0460.jpeg" },
    { id: 38, title: "Custom Pergola Build", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/rhfmenio_IMG_0461.jpeg" },
    { id: 39, title: "Commercial Fence Installation", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/lafu6yvo_IMG_0462.jpeg" },
    { id: 40, title: "Roof Installation - Aerial View", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/ug23sir7_IMG_0463.jpeg" },
    { id: 41, title: "Roof Installation", category: "Exterior", image: "https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/erosfi9p_IMG_0464.jpeg" },
  ];

  const categories = ["Featured", "All", "Interior", "Exterior", "Commercial", "Restoration", "Construction"];
  
  const getFilteredProjects = () => {
    if (activeFilter === "Featured") {
      return projects.filter(p => p.featured);
    } else if (activeFilter === "All") {
      return projects;
    } else {
      return projects.filter(p => p.category === activeFilter);
    }
  };

  const filteredProjects = getFilteredProjects();

  return (
    <section id="gallery" className="gallery-section" data-testid="gallery-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="overline">OUR WORK</p>
          <h2 className="section-title">PROJECT GALLERY</h2>
        </motion.div>

        <div className="gallery-filters" data-testid="gallery-filters">
          {categories.map(cat => (
            <button
              key={cat}
              className={`filter-btn ${activeFilter === cat ? 'active' : ''}`}
              onClick={() => setActiveFilter(cat)}
              data-testid={`filter-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="gallery-grid">
          {filteredProjects.map((project, index) => (
            <motion.div 
              key={project.id}
              className="gallery-item"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              data-testid={`gallery-item-${project.id}`}
            >
              <img src={project.image} alt={project.title} />
              <div className="gallery-overlay">
                <span className="gallery-category">{project.category}</span>
                <h4>{project.title}</h4>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Certifications Section
const Certifications = () => (
  <section className="certifications-section" data-testid="certifications-section">
    <div className="container">
      <div className="certifications-content">
        <p className="certifications-label">CERTIFIED & ACCREDITED</p>
        <div className="certifications-grid">
          <div className="cert-badge" data-testid="cert-iicrc-water">
            <Shield size={28} />
            <span>IICRC Water Restoration</span>
          </div>
          <div className="cert-badge" data-testid="cert-iicrc-mold">
            <Shield size={28} />
            <span>IICRC Mold Remediation</span>
          </div>
          <div className="cert-badge" data-testid="cert-mrc">
            <Award size={28} />
            <span>Licensed MRC Company</span>
          </div>
          <div className="cert-badge" data-testid="cert-bbb">
            <CheckCircle size={28} />
            <span>BBB Accredited</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Testimonials Section
const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([
    { id: 1, name: "Julie Benedict", rating: 5, review: "Art & his team were reliable and professional during a very stressful time. Knowing that they had things under control made our situation a lot easier to handle. Thank you!", service_type: "Restoration" },
    { id: 2, name: "Thais Gibson", rating: 5, review: "Giraffe did a fantastic job and Art was extremely communicative, thorough and went out of his way to ensure everything was done properly. Very grateful!", service_type: "Restoration" },
    { id: 3, name: "Patrick Dorman", rating: 5, review: "Giraffe came out promptly, gave me a fair quote and had the job done within a week. Extremely happy with the results!", service_type: "Construction" },
    { id: 4, name: "Edward Aguilar", rating: 5, review: "I reached out to Art & Sam at Giraffe Restoration for help with a potential mold issue in my bathroom, and I couldn't be more satisfied with the service. We scheduled a same-day free inspection, and Art and his team arrived promptly. They were very respectful of our home, even taking the time to remove their shoes before entering. Using specialized equipment, they thoroughly checked for any signs of mold and gave me a clear picture of the situation. What I appreciated most was that they offered several options based on their findings, but didn't pressure me into choosing one. I highly recommend Giraffe for anyone dealing with mold concerns!", service_type: "Mold Remediation" },
    { id: 5, name: "Madeline Ainsworth", rating: 5, review: "Excellent experience working with Giraffe. When we had a sewage leak and our carpet got wet, they were super quick to respond and acted quickly with the necessary remediation! Set up fans, got the carpet out, etc. Professional, friendly and communicative throughout the process. We appreciate you all & highly recommend!", service_type: "Water Damage" },
  ]);

  useEffect(() => {
    axios.get(`${API}/testimonials`)
      .then(res => {
        if (res.data.length > 0) setTestimonials(res.data);
      })
      .catch(console.error);
  }, []);

  return (
    <section id="testimonials" className="testimonials-section" data-testid="testimonials-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="overline">TESTIMONIALS</p>
          <h2 className="section-title">WHAT OUR CLIENTS SAY</h2>
        </motion.div>

        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={testimonial.id}
              className="testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`testimonial-${testimonial.id}`}
            >
              <div className="testimonial-rating">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={18} fill="#FF5A00" color="#FF5A00" />
                ))}
              </div>
              <p className="testimonial-text">"{testimonial.review}"</p>
              <div className="testimonial-author">
                <span className="author-name">{testimonial.name}</span>
                <span className="author-service">{testimonial.service_type}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
    request_type: "quote"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", service_type: "", message: "", request_type: "quote" });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting your request. Please try again or call us directly.");
    }
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="contact-section" data-testid="contact-section">
      <div className="container">
        <div className="contact-grid">
          <motion.div 
            className="contact-info"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="overline">GET IN TOUCH</p>
            <h2 className="section-title">
              REQUEST A<br />
              <span className="text-gradient">FREE QUOTE</span>
            </h2>
            <p className="contact-text">
              Ready to start your project? Fill out the form and we'll get back to you 
              within 24 hours. For emergencies, call us directly - we're available 24/7.
            </p>

            <div className="contact-details">
              <a href="tel:5127183553" className="contact-item" data-testid="contact-phone">
                <div className="contact-icon emergency">
                  <Phone size={24} />
                </div>
                <div>
                  <span className="contact-label">24/7 Emergency Line</span>
                  <span className="contact-value">(512) 718-3553</span>
                </div>
              </a>
              <a href="tel:5129935464" className="contact-item" data-testid="contact-phone-sam">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <div>
                  <span className="contact-label">Sam Rodriguez</span>
                  <span className="contact-value">(512) 993-5464</span>
                </div>
              </a>
              <a href="mailto:info@girafferestoration.com" className="contact-item" data-testid="contact-email">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div>
                  <span className="contact-label">Email Us</span>
                  <span className="contact-value">info@girafferestoration.com</span>
                </div>
              </a>
              <div className="contact-item" data-testid="contact-location">
                <div className="contact-icon">
                  <MapPin size={24} />
                </div>
                <div>
                  <span className="contact-label">Home Office</span>
                  <span className="contact-value">Liberty Hill, TX</span>
                </div>
              </div>
              <div className="contact-item" data-testid="contact-service-area">
                <div className="contact-icon">
                  <MapPin size={24} />
                </div>
                <div>
                  <span className="contact-label">Serving</span>
                  <span className="contact-value">The State of Texas & Beyond</span>
                </div>
              </div>
              <div className="contact-item" data-testid="contact-hours">
                <div className="contact-icon">
                  <Clock size={24} />
                </div>
                <div>
                  <span className="contact-label">Availability</span>
                  <span className="contact-value">24/7 Emergency Services</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="contact-form-wrapper"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {submitted ? (
              <div className="form-success" data-testid="form-success">
                <CheckCircle size={48} className="success-icon" />
                <h3>Thank You!</h3>
                <p>We've received your request and will contact you within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} className="btn-outline">
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="John Doe"
                    data-testid="input-name"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      placeholder="john@email.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="service">Service Needed *</label>
                    <select
                      id="service"
                      value={formData.service_type}
                      onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                      required
                      data-testid="select-service"
                    >
                      <option value="">Select a service</option>
                      <option value="water-damage">Water Damage Restoration</option>
                      <option value="fire-damage">Fire Damage Restoration</option>
                      <option value="mold">Mold Remediation</option>
                      <option value="construction">General Construction</option>
                      <option value="hvac">HVAC Services</option>
                      <option value="roofing">Roofing</option>
                      <option value="exterior">Exterior Construction</option>
                      <option value="interior">Interior Renovation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="request_type">Request Type</label>
                    <select
                      id="request_type"
                      value={formData.request_type}
                      onChange={(e) => setFormData({...formData, request_type: e.target.value})}
                      data-testid="select-request-type"
                    >
                      <option value="quote">Free Quote</option>
                      <option value="inspection">Free Inspection</option>
                      <option value="contact">General Inquiry</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={4}
                    placeholder="Tell us about your project or situation..."
                    data-testid="textarea-message"
                  />
                </div>
                <button 
                  type="submit" 
                  className="btn-primary full-width"
                  disabled={isSubmitting}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                  <Send size={18} />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Chat Widget
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: "Hi! Welcome to Giraffe Home & Commercial Services. I'm here to help you with any questions about our construction and restoration services. How can I assist you today?"
      }]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I'm having trouble connecting right now. Please call us directly at (555) 123-4567 for immediate assistance." 
      }]);
    }
    setIsLoading(false);
  };

  return (
    <>
      <button 
        className={`chat-fab ${isOpen ? "hidden" : ""}`}
        onClick={() => setIsOpen(true)}
        data-testid="chat-fab"
      >
        <MessageCircle size={24} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="chat-widget"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            data-testid="chat-widget"
          >
            <div className="chat-header">
              <div className="chat-header-info">
                <div className="chat-avatar">G</div>
                <div>
                  <h4>Giraffe Support</h4>
                  <span className="chat-status">Online • Typically replies instantly</span>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="chat-close" data-testid="chat-close">
                <X size={20} />
              </button>
            </div>

            <div className="chat-messages" data-testid="chat-messages">
              {messages.map((msg, index) => (
                <div key={index} className={`chat-message ${msg.role}`}>
                  {msg.content}
                </div>
              ))}
              {isLoading && (
                <div className="chat-message assistant loading">
                  <span className="typing-indicator">
                    <span></span><span></span><span></span>
                  </span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-wrapper">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type your message..."
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button 
                onClick={sendMessage} 
                disabled={isLoading || !inputValue.trim()}
                data-testid="chat-send"
              >
                <Send size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Footer
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-brand">
          <div className="logo">
            <img src="https://customer-assets.emergentagent.com/job_damage-restore-pro/artifacts/8w2t4a51_E2A5A613-25B8-4838-88DA-4BC61FBC2F68.png" alt="Giraffe Home & Commercial Services" className="logo-image" />
            <div className="logo-text-wrapper">
              <span className="logo-text">GIRAFFE</span>
              <span className="logo-sub">Home & Commercial Services LLC</span>
            </div>
          </div>
          <p>Your trusted partner for construction, restoration, and home services. Licensed, insured, and committed to excellence.</p>
        </div>

        <div className="footer-links">
          <h4>Services</h4>
          <ul>
            <li><a href="#services">Water Damage</a></li>
            <li><a href="#services">Fire Restoration</a></li>
            <li><a href="#services">Mold Remediation</a></li>
            <li><a href="#services">General Construction</a></li>
            <li><a href="#services">HVAC Services</a></li>
            <li><a href="#services">Roofing</a></li>
          </ul>
        </div>

        <div className="footer-links">
          <h4>Company</h4>
          <ul>
            <li><a href="#about">About Us</a></li>
            <li><a href="#gallery">Our Work</a></li>
            <li><a href="#testimonials">Reviews</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>

        <div className="footer-contact">
          <h4>Contact Us</h4>
          <a href="tel:5127183553" className="footer-phone">
            <Phone size={18} /> (512) 718-3553
          </a>
          <a href="mailto:info@girafferestoration.com">
            <Mail size={18} /> info@girafferestoration.com
          </a>
          <a href="mailto:sam@girafferestoration.com">
            <Mail size={18} /> sam@girafferestoration.com
          </a>
          <p><MapPin size={18} /> Liberty Hill, TX | The State of Texas & Beyond</p>
          <a href="https://www.instagram.com/giraffeatx" target="_blank" rel="noopener noreferrer" className="footer-social">
            <Instagram size={18} /> @giraffeatx
          </a>
        </div>
      </div>

      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Giraffe Home & Commercial Services LLC. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Main Home Page
const HomePage = () => {
  return (
    <div className="app-wrapper">
      <EmergencyBanner />
      <Header />
      <main>
        <Hero />
        <Services />
        <WhoWeServe />
        <About />
        <Certifications />
        <Gallery />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
